import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../../lib/supabase";

export interface ConversationTab {
  id: string;
  agent_id: string;
  title: string | null;
  is_pinned: boolean;
  closed_at: string | null;
  is_archived: boolean;
  last_activity_at: string;
  updated_at: string;
}

export const conversationTabsKey = (agentId: string) =>
  ["agent_conversations", "tabs", agentId] as const;

export function useConversationTabsQuery(agentId: string | null) {
  return useQuery({
    queryKey: agentId ? conversationTabsKey(agentId) : ["agent_conversations", "tabs", "_none"],
    enabled: !!agentId,
    queryFn: async (): Promise<ConversationTab[]> => {
      if (!agentId) return [];
      const { data, error } = await supabase
        .from("agent_conversations_view")
        .select(
          "id, agent_id, title, is_pinned, closed_at, is_archived, last_activity_at, updated_at",
        )
        .eq("agent_id", agentId)
        .is("closed_at", null)
        .eq("is_archived", false)
        .order("is_pinned", { ascending: false })
        .order("last_activity_at", { ascending: false });
      if (error) throw error;
      return (data ?? []).filter((row): row is ConversationTab => !!row.id && !!row.agent_id);
    },
    staleTime: 30_000,
  });
}

const ACTIVE_CONVO_KEY = "mannaActiveConvoByAgent";

function readActiveMap(): Record<string, string> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(ACTIVE_CONVO_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function writeActiveMap(map: Record<string, string>) {
  window.localStorage.setItem(ACTIVE_CONVO_KEY, JSON.stringify(map));
}

export function useActiveConversation(
  agentId: string | null,
  tabs: ConversationTab[],
): { activeConvoId: string | null; setActiveConvoId: (id: string | null) => void } {
  const [map, setMap] = useState<Record<string, string>>(readActiveMap);

  // Derive at render time: stored choice if it still exists, else the
  // first tab, else null when the agent has no conversations.
  const stored = agentId ? (map[agentId] ?? null) : null;
  const activeConvoId = (() => {
    if (!agentId) return null;
    if (!tabs.length) return null;
    if (stored && tabs.some((t) => t.id === stored)) return stored;
    return tabs[0]!.id;
  })();

  const setActiveConvoId = useCallback(
    (id: string | null) => {
      if (!agentId) return;
      setMap((prev) => {
        const next = { ...prev };
        if (id) next[agentId] = id;
        else delete next[agentId];
        writeActiveMap(next);
        return next;
      });
    },
    [agentId],
  );

  return { activeConvoId, setActiveConvoId };
}

export function useRenameConversation(agentId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ conversation_id, title }: { conversation_id: string; title: string }) => {
      const { error } = await supabase.rpc("rename_conversation", {
        payload: { conversation_id, title } as never,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      if (agentId) qc.invalidateQueries({ queryKey: conversationTabsKey(agentId) });
    },
  });
}

export function useCloseConversation(agentId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (conversation_id: string) => {
      const { error } = await supabase.rpc("close_conversation", {
        payload: { conversation_id } as never,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      if (agentId) qc.invalidateQueries({ queryKey: conversationTabsKey(agentId) });
    },
  });
}

export function useCreateConversation(agentId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<string> => {
      if (!agentId) throw new Error("agentId required");
      const { data, error } = await supabase.rpc("create_conversation", {
        payload: { agent_id: agentId } as never,
      });
      if (error) throw error;
      return data as unknown as string;
    },
    onSuccess: () => {
      if (agentId) qc.invalidateQueries({ queryKey: conversationTabsKey(agentId) });
    },
  });
}
